import { useState, useRef, useEffect } from 'react';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';
import { toast } from 'sonner';
import { Toaster } from '../components/ui/sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const HERO_VIDEO = 'https://assets.mixkit.co/videos/45780/45780-720.mp4';
const HERO_IMG = 'https://static.prod-images.emergentagent.com/jobs/7621458b-ca63-4125-ad1b-115cf548682c/images/e2bf7539380232c158edb21ac1619c8ff9791a16d08714d3b35212123f73cd7b.png';

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.15, delayChildren: 0.2 } }
};
const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] } }
};

export default function InvitationalPage() {
  const [form, setForm] = useState({ full_name: '', whatsapp: '', email: '', handicap: '', home_club: '', travel_international: '' });
  const [sending, setSending] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const formRef = useRef(null);

  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const heroY = useTransform(scrollYProgress, [0, 1], [0, 250]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);
  const smoothY = useSpring(heroY, { stiffness: 50, damping: 20 });

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.whatsapp || !form.email) {
      toast.error('Completa los campos requeridos');
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/invitational`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      if (res.ok) {
        setSubmitted(true);
      } else {
        const data = await res.json();
        toast.info(data.message || data.detail);
      }
    } catch { toast.error('Error de conexion'); }
    finally { setSending(false); }
  };

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const inputCls = "w-full bg-transparent border-b border-white/20 py-4 text-[15px] text-white placeholder:text-zinc-600 focus:outline-none focus:border-white/50 transition-colors duration-500";

  return (
    <div className="bg-black text-white min-h-screen selection:bg-white/10" data-testid="invitational-page">
      <Toaster position="bottom-center" theme="dark" />

      {/* ============ HERO ============ */}
      <section ref={heroRef} className="relative h-screen flex items-center justify-center overflow-hidden" data-testid="invitational-hero">
        <motion.video
          autoPlay muted loop playsInline poster={HERO_IMG}
          onCanPlay={() => setVideoLoaded(true)}
          className="absolute inset-0 w-full h-full object-cover"
          style={{ y: smoothY }}
        >
          <source src={HERO_VIDEO} type="video/mp4" />
        </motion.video>
        {!videoLoaded && <img src={HERO_IMG} alt="" className="absolute inset-0 w-full h-full object-cover" />}
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/50 to-black" />

        <motion.div className="relative z-10 text-center px-6 max-w-3xl" style={{ opacity: heroOpacity }}>
          <motion.p
            initial={{ opacity: 0, letterSpacing: '0.1em' }}
            animate={{ opacity: 1, letterSpacing: '0.3em' }}
            transition={{ delay: 0.5, duration: 1.2 }}
            className="text-[11px] text-zinc-400 font-medium uppercase mb-8"
          >
            Elite Golf MX — Invitational Access
          </motion.p>

          <motion.h1
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-light tracking-tighter text-white leading-[0.95]"
            style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}
          >
            <motion.span className="block overflow-hidden">
              <motion.span className="inline-block" initial={{ y: 100 }} animate={{ y: 0 }} transition={{ delay: 0.8, duration: 1, ease: [0.25, 0.46, 0.45, 0.94] }}>
                Experiencias de Golf Privadas.
              </motion.span>
            </motion.span>
            <motion.span className="block overflow-hidden">
              <motion.span className="inline-block" initial={{ y: 100 }} animate={{ y: 0 }} transition={{ delay: 0.95, duration: 1, ease: [0.25, 0.46, 0.45, 0.94] }}>
                En Todo el Mundo.
              </motion.span>
            </motion.span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.5, duration: 1 }}
            className="text-sm md:text-base text-zinc-400 mt-8 max-w-md mx-auto leading-relaxed font-light"
          >
            Has sido invitado a aplicar para las experiencias de golf personalizadas de Elite Golf MX.
          </motion.p>

          <motion.button
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 2, duration: 0.8 }}
            onClick={scrollToForm}
            className="mt-10 border border-white/30 text-white px-10 py-4 text-sm tracking-widest uppercase font-light hover:bg-white hover:text-black transition-all duration-700"
            data-testid="request-access-btn"
          >
            Solicitar Acceso
          </motion.button>
        </motion.div>

        {/* Scroll line */}
        <motion.div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-10"
          initial={{ opacity: 0 }} animate={{ opacity: 0.4 }} transition={{ delay: 2.5, duration: 1 }}>
          <motion.div className="w-px h-16 bg-gradient-to-b from-transparent to-white/40"
            animate={{ scaleY: [0, 1, 0] }} transition={{ repeat: Infinity, duration: 2, ease: 'easeInOut' }}
            style={{ transformOrigin: 'top' }}
          />
        </motion.div>
      </section>

      {/* ============ STATEMENT ============ */}
      <section className="py-28 md:py-40 px-6" data-testid="invitational-statement">
        <motion.div
          initial="hidden" whileInView="show" viewport={{ once: true, margin: '-100px' }}
          variants={stagger}
          className="max-w-2xl mx-auto text-center"
        >
          <motion.div variants={fadeUp} className="w-12 h-px bg-white/20 mx-auto mb-10" />
          <motion.p variants={fadeUp}
            className="text-lg md:text-xl lg:text-2xl font-light text-zinc-300 leading-relaxed tracking-tight"
            style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}
          >
            Elite Golf MX disena viajes de golf a medida para jugadores que buscan acceso a los campos mas iconicos y destinos exclusivos del mundo.
          </motion.p>
          <motion.div variants={fadeUp} className="w-12 h-px bg-white/20 mx-auto mt-10" />
        </motion.div>
      </section>

      {/* ============ FORM ============ */}
      <section ref={formRef} className="py-20 md:py-32 px-6" data-testid="invitational-form-section">
        <div className="max-w-lg mx-auto">
          {!submitted ? (
            <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger}>
              <motion.p variants={fadeUp} className="text-[11px] text-zinc-500 uppercase tracking-[0.3em] text-center mb-3">
                Aplicacion
              </motion.p>
              <motion.h2 variants={fadeUp}
                className="text-2xl md:text-3xl font-light tracking-tight text-white text-center mb-14"
                style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}
              >
                Acceso Invitacional
              </motion.h2>

              <motion.form variants={fadeUp} onSubmit={handleSubmit} className="space-y-0" data-testid="invitational-form">
                <div className="space-y-1">
                  <input type="text" placeholder="Nombre Completo *" value={form.full_name} onChange={e => update('full_name', e.target.value)}
                    className={inputCls} data-testid="inv-name" />
                </div>
                <div className="space-y-1">
                  <input type="tel" placeholder="Numero de WhatsApp *" value={form.whatsapp} onChange={e => update('whatsapp', e.target.value)}
                    className={inputCls} data-testid="inv-whatsapp" />
                </div>
                <div className="space-y-1">
                  <input type="email" placeholder="Email *" value={form.email} onChange={e => update('email', e.target.value)}
                    className={inputCls} data-testid="inv-email" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8">
                  <input type="text" placeholder="Handicap" value={form.handicap} onChange={e => update('handicap', e.target.value)}
                    className={inputCls} data-testid="inv-handicap" />
                  <input type="text" placeholder="Club de Golf Local" value={form.home_club} onChange={e => update('home_club', e.target.value)}
                    className={inputCls} data-testid="inv-club" />
                </div>
                <div className="pt-6">
                  <p className="text-[11px] text-zinc-500 uppercase tracking-wider mb-3">Viajarias internacionalmente por experiencias de golf?</p>
                  <div className="flex gap-4">
                    {['Si', 'Depende del destino'].map(opt => (
                      <button type="button" key={opt}
                        onClick={() => update('travel_international', opt)}
                        className={`px-5 py-2.5 text-sm transition-all duration-500 ${
                          form.travel_international === opt
                            ? 'bg-white text-black'
                            : 'border border-white/15 text-zinc-400 hover:border-white/40'
                        }`}
                        data-testid={`inv-travel-${opt === 'Si' ? 'yes' : 'depends'}`}
                      >
                        {opt}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="pt-12">
                  <button type="submit" disabled={sending}
                    className="w-full border border-white/30 text-white py-5 text-sm tracking-widest uppercase font-light hover:bg-white hover:text-black transition-all duration-700 disabled:opacity-30"
                    data-testid="inv-submit"
                  >
                    {sending ? 'Enviando...' : 'Enviar Aplicacion'}
                  </button>
                </div>
              </motion.form>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
              className="text-center py-16"
              data-testid="inv-success"
            >
              <div className="w-16 h-px bg-white/30 mx-auto mb-10" />
              <h3 className="text-2xl md:text-3xl font-light text-white tracking-tight mb-4" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                Aplicacion Recibida.
              </h3>
              <p className="text-sm text-zinc-500 max-w-xs mx-auto leading-relaxed">
                Nuestro equipo concierge contactara a los jugadores seleccionados.
              </p>
              <div className="w-16 h-px bg-white/30 mx-auto mt-10" />
            </motion.div>
          )}
        </div>
      </section>

      {/* ============ BRAND AUTHORITY ============ */}
      <section className="py-28 md:py-40 px-6" data-testid="invitational-authority">
        <motion.div
          initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 1 }}
          className="max-w-2xl mx-auto text-center"
        >
          <p className="text-sm md:text-base font-light text-zinc-500 leading-relaxed tracking-tight">
            Elite Golf MX se especializa en experiencias de golf a medida en los destinos mas prestigiosos del mundo,
            incluyendo Escocia, Estados Unidos, Japon, Europa y Mexico.
          </p>
        </motion.div>
      </section>

      {/* ============ MINIMAL FOOTER ============ */}
      <footer className="py-16 px-6 border-t border-white/5" data-testid="invitational-footer">
        <div className="max-w-lg mx-auto text-center space-y-3">
          <p className="text-sm tracking-[0.2em] font-light text-white" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            ELITE GOLF MX
          </p>
          <p className="text-xs text-zinc-600">Experiencias de Golf Personalizadas en Todo el Mundo</p>
          <p className="text-xs text-zinc-700">www.elitegolfmx.mx</p>
        </div>
      </footer>
    </div>
  );
}
